import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import axios from "axios";
import { buildApiUrl } from "../../apiConfig";
import { buildStatusApiUrl } from "../../statusApiConfig";
import {
  buildLp006Draft,
  getLp006FieldMaxLength,
  getLp006ContractSuffix,
  INVOICE_NO,
  LP006_FIELDS,
  LP006_TEMPLATE_ID,
  NOT_APPLICABLE,
  validateLp006Draft,
} from "./lp006Contract";

const sections = [...new Set(LP006_FIELDS.map((field) => field.section))];
const apiMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

export default function Lp006ContractForm({ contractData, handleInputChange }) {
  const [contractTier, setContractTier] = useState("basic");
  const [unitMeasure, setUnitMeasure] = useState(contractData.um || "1 BUC");
  const [draft, setDraft] = useState(() => buildLp006Draft(contractData));
  const dirty = useRef(new Set());
  const active = useRef(true);
  const inFlight = useRef(false);
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [pendingRequestId, setPendingRequestId] = useState("");
  const [blocked, setBlocked] = useState(false);
  const [sent, setSent] = useState(null);
  const [statusRevision, setStatusRevision] = useState(0);
  const [statusCheck, setStatusCheck] = useState({});
  const validation = validateLp006Draft(draft);
  const isPremium = contractTier === "premium";
  const provider = isPremium ? "SignNow" : "Zoho";
  const unitMeasureError =
    isPremium && (!unitMeasure.trim() || unitMeasure.trim().length > 2048)
      ? "Completează U/M cu cel mult 2048 de caractere."
      : "";
  const contractNumber = draft.nrContract.trim();
  const contractSuffix = getLp006ContractSuffix(contractNumber);
  const statusKey = `${contractNumber}:${statusRevision}`;

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    if (busy || pendingRequestId || sent || blocked) return;
    const incoming = buildLp006Draft(contractData);
    setDraft((current) =>
      Object.fromEntries(
        LP006_FIELDS.map(({ name }) => [
          name,
          dirty.current.has(name) ? current[name] : incoming[name],
        ]),
      ),
    );
  }, [contractData, busy, pendingRequestId, sent, blocked]);

  useEffect(() => {
    if (!contractSuffix || sent) return undefined;
    const controller = new AbortController();
    let current = true;
    setStatusCheck({ key: statusKey, loading: true });
    axios
      .get(
        buildApiUrl(
          `/api/tasks/cont-status/${encodeURIComponent(contractSuffix)}?exact=true`,
        ),
        { signal: controller.signal },
      )
      .then(({ data }) => {
        if (!current) return;
        if (!data?.status)
          throw new Error("Statusul contractului nu este disponibil.");
        setStatusCheck({ key: statusKey, loading: false, status: data.status });
      })
      .catch((error) => {
        if (!current) return;
        setStatusCheck({
          key: statusKey,
          loading: false,
          error:
            error.response?.status === 404
              ? "Contractul nu a fost găsit în Jira (CONT). Verifică numărul contractului înainte de trimitere."
              : apiMessage(
                  error,
                  "Nu am putut verifica statusul contractului în Jira. Reîncearcă verificarea.",
                ),
        });
      });
    return () => {
      current = false;
      controller.abort();
    };
  }, [contractSuffix, statusKey, sent]);

  const statusCurrent = statusCheck.key === statusKey;
  const checkingStatus =
    Boolean(contractSuffix) && (!statusCurrent || statusCheck.loading);
  const statusError = statusCurrent ? statusCheck.error : "";
  const alreadyCreated =
    statusCurrent && ["22_CREAT", "33_SEMNAT"].includes(statusCheck.status);
  const locked =
    busy ||
    Boolean(pendingRequestId) ||
    blocked ||
    Boolean(sent) ||
    alreadyCreated;

  const changeField = (name, value) => {
    dirty.current.add(name);
    setDraft((current) => ({ ...current, [name]: value }));
    setFeedback(null);
  };

  const changeTier = (_event, nextTier) => {
    if (!nextTier || locked || inFlight.current) return;
    setContractTier(nextTier);
    setFeedback(null);
  };

  const advanceStatus = async (record) => {
    await axios.post(
      buildStatusApiUrl(
        `/api/tasks/set-status/${encodeURIComponent(getLp006ContractSuffix(record.contractNumber))}?exact=true`,
      ),
      { status: "22_CREAT" },
    );
    if (!active.current) return;
    setSent({ ...record, statusUpdated: true });
    setFeedback({
      severity: "success",
      message: `Contractul LP006 a fost trimis prin ${record.provider}, iar statusul CONT a fost actualizat.`,
    });
  };

  const handleFailure = (body, fallback, uncertain = false) => {
    if (!active.current) return;
    if (body?.request_id) setPendingRequestId(String(body.request_id));
    const mustReconcile =
      uncertain ||
      body?.retryable === false ||
      body?.code === "LP006_CREATE_UNCERTAIN" ||
      (body?.request_id && body.recoverable !== true);
    setBlocked(Boolean(mustReconcile));
    setFeedback({
      severity: "error",
      message: body?.message || body?.error || fallback,
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (
      inFlight.current ||
      blocked ||
      sent ||
      alreadyCreated ||
      checkingStatus ||
      statusError
    )
      return;
    setShowErrors(true);
    if (!validation.isValid || unitMeasureError) return;
    inFlight.current = true;
    setBusy(true);
    setFeedback(null);
    const fields = validation.fields;
    let submittedRecord;
    try {
      let requestId = null;
      if (isPremium) {
        const tipContract = contractData.tipContract;
        const response = await axios.post(
          buildApiUrl("/api/contracts/signnow/workflow"),
          {
            documentName: `${tipContract}_${fields.nume}_${fields.prenume}_${fields.nrContract}`,
            recipient1Email: fields.email,
            recipient1Name: `${fields.nume} ${fields.prenume}`.trim(),
            recipientPhone: fields.telefon,
            Tip_Contract: tipContract,
            nrContract: fields.nrContract,
            smartFieldsData: Object.entries({
              ...fields,
              NrContract: fields.nrContract,
              tipContract,
              um: unitMeasure.trim(),
              consignatar: "Mirela Bucurei",
            }).map(([name, value]) => ({ [name]: value })),
          },
        );
        // The existing SignNow workflow confirms both the invite and its SMS
        // with this response. It does not expose a recoverable document ID.
        if (response.data?.message !== "SUCCES: Contract trimis cu succes!") {
          handleFailure(
            null,
            "SignNow nu a confirmat trimiterea contractului. Statusul CONT nu a fost modificat.",
            true,
          );
          return;
        }
      } else {
        const response = await axios.post(
          buildApiUrl(
            `/api/contracte/zoho/templates/${LP006_TEMPLATE_ID}/create`,
          ),
          {
            fields,
            signer_email: fields.email,
            signer_name: `${fields.nume} ${fields.prenume}`.trim(),
            request_name: `LP006_${fields.nume}_${fields.prenume}_${fields.nrContract}`,
            auto_submit: true,
            ...(pendingRequestId
              ? { existing_request_id: pendingRequestId }
              : {}),
          },
        );
        const result = response.data;
        if (
          result?.ok !== true ||
          result.submitted !== true ||
          !result.request_id
        ) {
          handleFailure(
            result,
            "Zoho nu a confirmat trimiterea contractului. Statusul CONT nu a fost modificat.",
            !result?.request_id,
          );
          return;
        }
        requestId = String(result.request_id);
      }
      submittedRecord = {
        requestId,
        provider,
        contractNumber: fields.nrContract,
        statusUpdated: false,
      };
      if (active.current) {
        setSent(submittedRecord);
        setPendingRequestId("");
      }
      await advanceStatus(submittedRecord);
    } catch (error) {
      if (submittedRecord) {
        if (active.current)
          setFeedback({
            severity: "warning",
            message: `Contractul a fost trimis prin ${submittedRecord.provider}, dar statusul CONT nu a putut fi actualizat. Reîncearcă doar actualizarea statusului.`,
          });
      } else {
        handleFailure(
          error.response?.data,
          `Trimiterea contractului nu a putut fi confirmată. Verifică documentul în ${provider} înainte de o nouă încercare.`,
          isPremium || !error.response,
        );
      }
    } finally {
      inFlight.current = false;
      if (active.current) setBusy(false);
    }
  };

  const retryStatus = async () => {
    if (!sent || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await advanceStatus(sent);
    } catch (error) {
      if (active.current)
        setFeedback({
          severity: "warning",
          message: apiMessage(
            error,
            `Statusul CONT nu a putut fi actualizat. Documentul ${sent.provider} este deja trimis.`,
          ),
        });
    } finally {
      inFlight.current = false;
      if (active.current) setBusy(false);
    }
  };

  return (
    <Box
      component="form"
      aria-label="Creare contract LP006"
      onSubmit={submit}
      noValidate
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        bgcolor: isPremium ? "#f7d6f4" : "#f3f7fd",
        border: `1px solid ${isPremium ? "#e5b5de" : "#d4e1f4"}`,
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 2,
            borderRadius: 2,
            bgcolor: "rgba(255, 255, 255, 0.7)",
          }}
        >
          <ToggleButtonGroup
            value={contractTier}
            exclusive
            onChange={changeTier}
            disabled={locked}
            size="small"
            aria-label="Varianta contractului LP006"
            sx={{
              bgcolor: "#ffffff",
              borderRadius: 999,
              p: "4px",
              boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
                color: "#2d2d2d",
              },
              "& .Mui-selected": {
                bgcolor: isPremium ? "#c218a3" : "#2d6cdf",
                color: "#ffffff",
              },
              "& .Mui-selected:hover": {
                bgcolor: isPremium ? "#a5148c" : "#2458b7",
              },
            }}
          >
            <ToggleButton type="button" value="premium">
              Premium
            </ToggleButton>
            <ToggleButton type="button" value="basic">
              Basic
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box>
          <Chip
            label={`LP006 · ${isPremium ? "Premium · SignNow" : "Basic · Zoho Sign"}`}
            color="primary"
            size="small"
          />
          <Typography variant="h6" sx={{ mt: 1, fontWeight: 800 }}>
            Contract FAST Sale 24h
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Completează câmpurile obligatorii marcate cu *. Codul interior al
            produsului (Designer code) este opțional.
          </Typography>
        </Box>
        <TextField
          label="TIP Contract"
          name="tipContract"
          value={contractData.tipContract || ""}
          onChange={handleInputChange}
          disabled={locked}
          fullWidth
        />
        {checkingStatus && !sent && (
          <Alert icon={<CircularProgress size={18} />} severity="info">
            Se verifică statusul contractului în Jira…
          </Alert>
        )}
        {statusError && !sent && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                onClick={() => setStatusRevision((value) => value + 1)}
              >
                Reîncearcă
              </Button>
            }
          >
            {statusError}
          </Alert>
        )}
        {alreadyCreated && !sent && (
          <Alert severity="info">
            Contractul este deja creat sau semnat. Nu este necesară o nouă
            trimitere.
          </Alert>
        )}
        {sections.map((section) => (
          <Box key={section}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1.5, fontWeight: 800, color: "#244b7e" }}
            >
              {section}
            </Typography>
            <Grid container spacing={2}>
              {isPremium && section === "Detalii produs" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="U/M"
                    name="um"
                    value={unitMeasure}
                    onChange={(event) => setUnitMeasure(event.target.value)}
                    disabled={locked}
                    required
                    fullWidth
                    inputProps={{ maxLength: 2048 }}
                    error={showErrors && Boolean(unitMeasureError)}
                    helperText={(showErrors && unitMeasureError) || " "}
                  />
                </Grid>
              )}
              {LP006_FIELDS.filter((field) => field.section === section).map(
                (field) => {
                  const isNotApplicable =
                    (field.conditional === "invoice" &&
                      draft.detinFactura === INVOICE_NO) ||
                    (field.conditional === "certificate" &&
                      draft.detinCertificat === "NU");
                  if (isNotApplicable)
                    return (
                      <Grid item xs={12} key={field.name}>
                        <Typography variant="body2" color="text.secondary">
                          {field.label}: {NOT_APPLICABLE}.
                        </Typography>
                      </Grid>
                    );
                  const error =
                    showErrors || dirty.current.has(field.name)
                      ? validation.errors[field.name]
                      : "";
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={
                        field.multiline || field.name === "detinFactura"
                          ? 12
                          : 6
                      }
                      key={field.name}
                    >
                      <TextField
                        name={field.name}
                        label={field.label}
                        required={!field.optional}
                        fullWidth
                        value={draft[field.name]}
                        onChange={(event) =>
                          changeField(field.name, event.target.value)
                        }
                        disabled={locked}
                        type={field.type === "date" ? "date" : "text"}
                        select={field.type === "select"}
                        multiline={field.multiline}
                        minRows={field.multiline ? 3 : undefined}
                        InputLabelProps={
                          field.type === "date" ? { shrink: true } : undefined
                        }
                        inputProps={{
                          maxLength: getLp006FieldMaxLength(field.name),
                          ...(field.name === "pretFinal"
                            ? { inputMode: "decimal" }
                            : {}),
                        }}
                        error={Boolean(error)}
                        helperText={error || field.helperText || " "}
                      >
                        {field.options?.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  );
                },
              )}
            </Grid>
          </Box>
        ))}
        {showErrors && (!validation.isValid || unitMeasureError) && (
          <Alert severity="error">
            Completează sau corectează cele{" "}
            {Object.keys(validation.errors).length + (unitMeasureError ? 1 : 0)}{" "}
            câmpuri marcate înainte de trimitere.
          </Alert>
        )}
        {feedback && (
          <Alert severity={feedback.severity}>{feedback.message}</Alert>
        )}
        {blocked && (
          <Alert severity="warning">
            Rezultatul necesită verificarea documentului în {provider}. O nouă
            creare este blocată pentru a evita un contract duplicat.
          </Alert>
        )}
        {pendingRequestId && !blocked && (
          <Alert severity="info">
            Documentul Zoho există ca draft. Reluarea folosește același
            document.
          </Alert>
        )}
        {sent ? (
          !sent.statusUpdated && (
            <Button onClick={retryStatus} disabled={busy} variant="contained">
              Reîncearcă actualizarea statusului CONT
            </Button>
          )
        ) : (
          <Button
            type="submit"
            variant="contained"
            disabled={
              busy ||
              blocked ||
              alreadyCreated ||
              checkingStatus ||
              Boolean(statusError)
            }
          >
            {busy
              ? "Se trimite contractul…"
              : blocked
                ? "Trimitere blocată"
                : pendingRequestId
                  ? "Reia trimiterea documentului Zoho"
                  : `Trimite contractul prin ${provider}`}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
